import { isRouteMissingError } from "@/lib/api-types";
import type { HttpClient } from "@/lib/http";
import { http } from "@/lib/http";

import type { PostJobValues } from "@/features/jobs/schemas";
import type {
  AddPlannedTasksInput,
  ApplicantsQuery,
  ApplicantsResult,
  ApplyVerification,
  BucketsResult,
  CreateJobResult,
  FinishedTaskDetail,
  FinishedTasksResult,
  JobDetail,
  JobInvitation,
  PostedOffer,
  JobType,
  JobSearchParams,
  JobSearchResult,
  JobSharing,
  MyJobsFilter,
  MyJobsResult,
  PlannedTasksQuery,
  PlannedTasksResult,
  PlatformStats,
  ReviewTasksInput,
  SharedJobsResult,
  SharedUser,
  ShareType,
  SubmitJobTaskPayload,
} from "@/features/jobs/types";

import type {
  ApplicantsResponseDto,
  ApplyVerifyDto,
  BucketsResponseDto,
  CreateJobResponseDto,
  FinishedTaskDetailDto,
  FinishedTasksDto,
  InvitationsDto,
  MyOffersDto,
  JobDetailDto,
  JobSearchResponseDto,
  MyJobsResponseDto,
  PlannedTasksResponseDto,
  PlatformStatsDto,
  SharedJobsResponseDto,
  SharingResponseDto,
} from "./dto";
import {
  mapApplicants,
  mapApplyVerification,
  mapBuckets,
  mapFinishedTaskDetail,
  mapFinishedTasks,
  mapInvitations,
  mapOffers,
  mapJobDetail,
  mapJobSearch,
  mapMyJobs,
  mapPlannedTasks,
  mapPlatformStats,
  mapSharedJobs,
  mapSharedUser,
  mapSharing,
} from "./mappers";

/**
 * Transport for the jobs browse/detail/apply slice, mirroring the legacy
 * `/api/v1/job/*` endpoints. v5 calls the backend directly (ADR-0003) instead
 * of proxying through Next API routes like the legacy app did.
 */
export interface JobsRepository {
  searchJobs(params: JobSearchParams): Promise<JobSearchResult>;
  getJobDetail(jobId: string): Promise<JobDetail>;
  getApplyVerification(jobId: string): Promise<ApplyVerification>;
  /** Returns the backend success message (e.g. for the completion screen). */
  submitJobTask(payload: SubmitJobTaskPayload): Promise<void>;
  /** Platform-wide device/job success matrix + top earners (`/jobs` stats tab). */
  getPlatformStats(): Promise<PlatformStats>;
  /** Jobs the worker was invited to (`/jobs_invited`). */
  getInvitations(): Promise<JobInvitation[]>;
  declineInvitation(inviteId: string): Promise<void>;

  /** Posted offers for `/myoffers` (demo-only — not a live backend feature). */
  getMyOffers(): Promise<PostedOffer[]>;
  /** Paginated finished/submitted tasks + status tallies (`/jobs_finished`). */
  getFinishedTasks(page: number): Promise<FinishedTasksResult>;
  /** Delete a still-pending submitted task; returns the refreshed page. */
  deleteFinishedTask(taskId: string): Promise<FinishedTasksResult>;
  getFinishedTaskDetail(taskId: string): Promise<FinishedTaskDetail>;
  /** Resubmit proof for a task the employer marked "not complete". */
  resubmitTask(taskId: string, jobProof: string): Promise<void>;
  /** Post a new job from the create wizard (`POST /api/v1/job/create`). */
  createJob(values: PostJobValues): Promise<CreateJobResult>;
  /** The employer's own jobs, filtered by status tab (`POST /api/v1/job/myjobs`). */
  getMyJobs(filter: MyJobsFilter, page: number): Promise<MyJobsResult>;
  /** Jobs other employers have shared with the current user ("Shared With Me"),
   * paginated independently from owned jobs via `shared_page`. */
  getSharedJobs(page: number): Promise<SharedJobsResult>;
  /** Activate (resume) or deactivate (pause) one of the employer's jobs. */
  setJobActive(jobId: string, active: boolean): Promise<void>;
  /** Permanently delete one of the employer's jobs. */
  deleteJob(jobId: string): Promise<void>;
  /** Submitted tasks for one of the employer's jobs (`/job_applicants`), with
   * server-side search/sort/paging. */
  getApplicants(
    jobId: string,
    query: ApplicantsQuery,
  ): Promise<ApplicantsResult>;
  /** A single submitted task for review (`/job_applicants/details`). */
  getApplicantTask(jobId: string, taskId: string): Promise<ApplicantsResult>;
  /** Export submitted tasks as CSV text (`GET /api/v1/job/job_applicants`). */
  downloadApplicantsCsv(
    jobId: string,
    filter: ApplicantsCsvFilter,
  ): Promise<string>;
  /** Approve/decline one or more submitted tasks. */
  reviewTasks(input: ReviewTasksInput): Promise<void>;
  /** Collaborators a job is shared with, grouped by access level. */
  getSharing(jobId: string): Promise<JobSharing>;
  /** Grant a user view/edit access to a job; returns the added collaborator. */
  addShare(
    jobId: string,
    username: string,
    type: ShareType,
  ): Promise<SharedUser>;
  /** Revoke a user's view/edit access to a job. */
  removeShare(jobId: string, userId: string, type: ShareType): Promise<void>;
  /** A page of device buckets for an automation job (`GET /api/v1/bucket/:jobId`). */
  getBuckets(jobId: string, page: number): Promise<BucketsResult>;
  /** Overwrite one device bucket's data (`PUT /api/v1/bucket/:jobId/:deviceId`). */
  updateBucket(
    jobId: string,
    deviceId: string,
    data: Record<string, unknown>,
  ): Promise<void>;
  /** Delete one device bucket (`DELETE /api/v1/bucket/:jobId/:deviceId`). */
  deleteBucket(jobId: string, deviceId: string): Promise<void>;
  /** Delete every bucket for a job (`DELETE /api/v1/bucket/:jobId`). */
  deleteAllBuckets(jobId: string): Promise<void>;
  /** A page of planned tasks for an automation job (`GET /planned_tasks`). */
  getPlannedTasks(
    jobId: string,
    query: PlannedTasksQuery,
  ): Promise<PlannedTasksResult>;
  /** Queue one or more planned tasks (`POST /planned_tasks`). */
  addPlannedTasks(input: AddPlannedTasksInput): Promise<void>;
  /** Delete one planned task (`DELETE /planned_tasks`). */
  deletePlannedTask(plannedTaskId: string): Promise<void>;
  /** Export planned tasks as CSV text (`GET /download_planned_tasks`). */
  downloadPlannedTasksCsv(jobId: string): Promise<string>;
}

/** Which submitted tasks the CSV export should include. */
export type ApplicantsCsvFilter = "all" | "pending";

/** How many buckets a page of the buckets tab shows (matches the legacy limit). */
const BUCKETS_PAGE_SIZE = 20;

/** Map a `/myjobs` tab to the legacy status filter body. */
function toMyJobsBody(filter: MyJobsFilter, page: number) {
  const status =
    filter === "pending"
      ? { $in: ["pending", "inactive", "declined"] }
      : filter;
  return { status, page };
}

/** Map the flat wizard form values to the legacy create-job request body. */
function toCreateBody(values: PostJobValues): Record<string, unknown> {
  const targets = values.worldwide ? ["worldwide"] : values.countries;
  const base = {
    job_type: values.job_type,
    title: values.title.trim(),
    category: values.category,
    description: values.description.trim(),
    duration: values.duration,
    job_price: values.job_price,
    Target_Workers: targets,
    allowedDeviceCountries: targets,
    premium: values.premium,
    featured: values.featured,
  };

  if (values.job_type === "devices_automation") {
    return {
      ...base,
      payment_type: values.payment_type,
      automation_id: values.automation_id.trim(),
      network_type: values.network_type,
      // Fan the single `device_source` choice back out to the three wire fields
      // the backend reads, sending the other two empty. It rejects anything but
      // exactly one populated source with `INVALID_DEVICE_SELECTION`, and
      // omitting all three — which this body used to do — counts as none, so
      // every automation post failed. See `JOB_DEVICE_SOURCES`.
      allow_devices_from_market: values.device_source === "market",
      allowed_workers:
        values.device_source === "user" ? values.allowed_workers : [],
      allowed_devices:
        values.device_source === "own" ? values.allowed_devices : [],
    };
  }

  return {
    ...base,
    proof: values.proof.trim(),
    is_proof_file: values.is_proof_file,
    positions: values.positions,
    few_times: values.few_times,
  };
}

/**
 * What "View: All" sends as `limit`.
 *
 * The backend only understands a number. Probed against the live route
 * 2026-08-14 with `totalCount:50` in play:
 *
 *   limit 50 / 100 / 200 / 500 / 1000 → 44 rows   (its real ceiling)
 *   limit "all"                       → 10 rows   (unparsed → default page)
 *   limit 0                           → 10 rows
 *   limit -1                          → 500
 *
 * So the literal string quietly did the opposite of what the option promised:
 * "All" returned the *smallest* result set of any choice in the dropdown. A
 * bounded number gets everything the backend will part with, and keeps the
 * request finite as the platform grows.
 */
export const ALL_JOBS_LIMIT = 1000;

/** Map domain search params to the legacy backend body keys. */
function toSearchBody(params: JobSearchParams) {
  return {
    page: params.page,
    limit: params.limit === "all" ? ALL_JOBS_LIMIT : params.limit,
    SortBY: params.sortBy ?? "added",
    orientation: params.orientation ?? "desc",
  };
}

export function createJobsRepository(client: HttpClient): JobsRepository {
  return {
    async searchJobs(params) {
      const dto = await client.post<JobSearchResponseDto>(
        "/api/v1/job/search",
        toSearchBody(params),
      );
      return mapJobSearch(dto);
    },
    async getJobDetail(jobId) {
      const dto = await client.get<JobDetailDto>(
        `/api/v1/job/job_details?job_id=${encodeURIComponent(jobId)}`,
      );
      return mapJobDetail(dto);
    },
    async getApplyVerification(jobId) {
      const dto = await client.get<ApplyVerifyDto>(
        `/api/v1/job/job_apply?job_id=${encodeURIComponent(jobId)}`,
      );
      return mapApplyVerification(dto);
    },
    async submitJobTask(payload) {
      await client.post("/api/v1/job/submit_JobTask", payload);
    },
    async getPlatformStats() {
      const dto = await client.get<PlatformStatsDto>(
        "/api/v1/job/top_jobs_success_rates",
      );
      return mapPlatformStats(dto);
    },
    async getInvitations() {
      const dto = await client.get<InvitationsDto>("/api/v1/job/invitations");
      return mapInvitations(dto);
    },
    async declineInvitation(inviteId) {
      await client.post("/api/v1/job/decline_Invitation", {
        invite_id: inviteId,
      });
    },
    async getMyOffers() {
      try {
        const dto = await client.get<MyOffersDto>("/api/v1/job/my_offers");
        return mapOffers(dto);
      } catch (error) {
        // Offers are not a backend feature: `GET /api/v1/job/my_offers` answers
        // a bare `404 Route does not exist` on the live backend (verified
        // 2026-08-11), and legacy `/myoffers` never fetched at all — it renders
        // a static "No offers have been posted yet." stub. v5 invented the
        // endpoint for the demo cards, so on every real deploy the query
        // rejected and the page showed "We couldn't load your offers" where
        // xgodo.com shows a clean empty state.
        //
        // A missing route therefore means "you have no offers", which is the
        // truth for every account today. Only 404 is absorbed — a 500, a
        // timeout or a 401 still surfaces the error/login state, and if the
        // backend ever implements the route this starts serving real data.
        if (isRouteMissingError(error)) return [];
        throw error;
      }
    },
    async getFinishedTasks(page) {
      const dto = await client.post<FinishedTasksDto>(
        "/api/v1/job/jobs_finished",
        { page, exclude_failed_automation_tasks: true },
      );
      return mapFinishedTasks(dto);
    },
    async deleteFinishedTask(taskId) {
      const dto = await client.del<FinishedTasksDto>(
        "/api/v1/job/jobs_finished",
        {
          body: JSON.stringify({ task_id: taskId }),
          headers: { "Content-Type": "application/json" },
        },
      );
      return mapFinishedTasks(dto);
    },
    async getFinishedTaskDetail(taskId) {
      const dto = await client.post<FinishedTaskDetailDto>(
        `/api/v1/job/jobs_finished?task_id=${encodeURIComponent(taskId)}`,
      );
      return mapFinishedTaskDetail(dto);
    },
    async resubmitTask(taskId, jobProof) {
      await client.post("/api/v1/job/task/resubmit", {
        job_task_id: taskId,
        job_proof: jobProof,
      });
    },
    async createJob(values) {
      const dto = await client.post<CreateJobResponseDto>(
        "/api/v1/job/create",
        toCreateBody(values),
      );
      return {
        jobId: String(dto.jobId ?? dto.job_id ?? ""),
        message: dto.message || "Your job has been posted.",
      };
    },
    async getMyJobs(filter, page) {
      const dto = await client.post<MyJobsResponseDto>(
        "/api/v1/job/myjobs",
        toMyJobsBody(filter, page),
      );
      return mapMyJobs(dto);
    },
    async getSharedJobs(page) {
      const dto = await client.post<SharedJobsResponseDto>(
        "/api/v1/job/myjobs",
        { shared_page: page },
      );
      return mapSharedJobs(dto);
    },
    async setJobActive(jobId, active) {
      await client.put("/api/v1/job", {
        job_id: jobId,
        action_type: active ? "active" : "inactive",
      });
    },
    async deleteJob(jobId) {
      await client.del("/api/v1/job", {
        body: JSON.stringify({ job_id: jobId }),
        headers: { "Content-Type": "application/json" },
      });
    },
    async getApplicants(jobId, query) {
      const dto = await client.post<ApplicantsResponseDto>(
        "/api/v1/job/job_applicants",
        {
          job_id: jobId,
          page: query.page,
          pagesize: query.pageSize,
          search: query.search.trim(),
          // The backend sorts worker-name by the underlying worker_id column.
          sortby: query.sortBy === "workerName" ? "worker_id" : query.sortBy,
          order: query.order,
        },
      );
      return { ...mapApplicants(dto), jobId };
    },
    async getApplicantTask(jobId, taskId) {
      const dto = await client.post<ApplicantsResponseDto>(
        "/api/v1/job/job_applicants",
        { job_id: jobId, task_id: taskId },
      );
      return { ...mapApplicants(dto), jobId };
    },
    async downloadApplicantsCsv(jobId, filter) {
      // The CSV endpoint responds with text/csv (not JSON); the HTTP client
      // returns the raw string body when it can't parse it as JSON.
      return client.get<string>(
        `/api/v1/job/job_applicants?jobId=${encodeURIComponent(jobId)}&filter=${filter}`,
      );
    },
    async reviewTasks({ jobId, taskIds, status, comment }) {
      await client.put("/api/v1/job/job_applicants", {
        job_id: jobId,
        JobTasks_Ids: taskIds,
        status,
        ...(comment ? { comment } : {}),
      });
    },
    async getSharing(jobId) {
      const dto = await client.get<SharingResponseDto>(
        `/api/v1/job/sharing?job_id=${encodeURIComponent(jobId)}`,
      );
      return mapSharing(dto);
    },
    async addShare(jobId, username, type) {
      const dto = await client.post<SharingResponseDto>("/api/v1/job/sharing", {
        job_id: jobId,
        username,
        type,
      });
      // The backend echoes the resolved user (id + canonical username); fall
      // back to the typed-in name so the chip still renders if it's omitted.
      return dto.user ? mapSharedUser(dto.user) : { userId: "", username };
    },
    async removeShare(jobId, userId, type) {
      await client.del("/api/v1/job/sharing", {
        body: JSON.stringify({ job_id: jobId, user_id: userId, type }),
        headers: { "Content-Type": "application/json" },
      });
    },
    async getBuckets(jobId, page) {
      const dto = await client.get<BucketsResponseDto>(
        `/api/v1/bucket/${encodeURIComponent(jobId)}?page=${page}&limit=${BUCKETS_PAGE_SIZE}`,
      );
      return mapBuckets(dto);
    },
    async updateBucket(jobId, deviceId, data) {
      await client.put(
        `/api/v1/bucket/${encodeURIComponent(jobId)}/${encodeURIComponent(deviceId)}`,
        { data },
      );
    },
    async deleteBucket(jobId, deviceId) {
      await client.del(
        `/api/v1/bucket/${encodeURIComponent(jobId)}/${encodeURIComponent(deviceId)}`,
      );
    },
    async deleteAllBuckets(jobId) {
      await client.del(`/api/v1/bucket/${encodeURIComponent(jobId)}`);
    },
    async getPlannedTasks(jobId, query) {
      const params = new URLSearchParams({
        job_id: jobId,
        page: String(query.page),
        limit: String(query.limit),
        search: query.search,
        sortOrder: query.sortOrder,
      });
      const dto = await client.get<PlannedTasksResponseDto>(
        `/api/v1/job/planned_tasks?${params.toString()}`,
      );
      return mapPlannedTasks(dto);
    },
    async addPlannedTasks({ jobId, inputs, remoteDeviceId, runImmediately }) {
      await client.post("/api/v1/job/planned_tasks", {
        job_id: jobId,
        // The backend stores each entry as a JSON string.
        inputs: inputs.map((entry) => JSON.stringify(entry)),
        ...(remoteDeviceId ? { remote_device_id: remoteDeviceId } : {}),
        run_immediately: Boolean(runImmediately),
      });
    },
    async deletePlannedTask(plannedTaskId) {
      await client.del("/api/v1/job/planned_tasks", {
        body: JSON.stringify({ planned_task_ids: [plannedTaskId] }),
        headers: { "Content-Type": "application/json" },
      });
    },
    async downloadPlannedTasksCsv(jobId) {
      return client.get<string>(
        `/api/v1/job/download_planned_tasks?jobId=${encodeURIComponent(jobId)}`,
      );
    },
  };
}

/** Default repository wired to the app's singleton HTTP client. */
export const jobsRepository = createJobsRepository(http);
